import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { range } from '@sorare/core/src/lib/arrays';

import { AppearanceAvatarWithScore } from 'components/game/Game/AppearanceAvatarWithScore';

import type {
  GameCardsPreviewQuery,
  GameCardsPreviewQueryVariables,
  GameCardsPreview_cardWithPlayerGameScoreInterface,
  GameCardsPreview_gameWithCardsPreview,
} from './__generated__/index.graphql';

const cardWithPlayerGameScoreInterfaceFragment = gql`
  fragment GameCardsPreview_cardWithPlayerGameScoreInterface on AnyCardWithPlayerGameScore {
    id
    card {
      slug
      anyPlayer {
        slug
      }
    }
    ...AppearanceAvatarWithScore_cardWithPlayerGameScoreInterface
  }
  ${AppearanceAvatarWithScore.fragments.cardWithPlayerGameScoreInterface}
` as TypedDocumentNode<GameCardsPreview_cardWithPlayerGameScoreInterface>;

const GAME_CARDS_PAGINATED = gql`
  query GameCardsPreviewQuery(
    $gameId: ID!
    $after: String
    $rarities: [Rarity!]
  ) {
    anyGame(id: $gameId) {
      id
      myBestByPlayerCards(first: 10, after: $after, rarities: $rarities) {
        nodes {
          id
          ...GameCardsPreview_cardWithPlayerGameScoreInterface
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        totalCount
      }
    }
  }
  ${cardWithPlayerGameScoreInterfaceFragment}
` as TypedDocumentNode<GameCardsPreviewQuery, GameCardsPreviewQueryVariables>;

const Appearances = styled(Horizontal)`
  padding: var(--unit);
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
`;

const StyledBrandXS = styled(BrandXS)`
  background: var(--c-nd-50);
  border-radius: 50%;
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
`;

const Loader = styled(Skeleton)`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
  flex-shrink: 0;
`;

type Props = {
  gameWithCardsPreview: GameCardsPreview_gameWithCardsPreview;
  cardsRarities: Rarity[];
  onCardClick: (playerSlug: string) => void;
};

// First, we show first N cards with the data coming from main query (gameWithCardsPreview). Then we load more data from the paginated query on user action
// This avoid triggering Y (= game counts) queries on page load
export const GameCardsPreview = ({
  gameWithCardsPreview,
  cardsRarities,
  onCardClick,
}: Props) => {
  const [usePaginatedData, setUsePaginatedData] = useState(false);
  const { data, loading, loadMore } = usePaginatedQuery(GAME_CARDS_PAGINATED, {
    variables: {
      gameId: gameWithCardsPreview.game.id,
      rarities: cardsRarities,
    },
    connection: 'AnyCardWithPlayerGameScoreConnection',

    skip: !usePaginatedData,
  });

  const paginatedcardsWithPlayerGameScores =
    data?.anyGame?.myBestByPlayerCards?.nodes;

  const {
    cardsWithPlayerGameScores: firstCardsWithPlayerGameScores,
    totalCount: totalCountFromProps,
  } = gameWithCardsPreview;

  if (!firstCardsWithPlayerGameScores) return null;

  const cardsWithPlayerGameScores =
    paginatedcardsWithPlayerGameScores || firstCardsWithPlayerGameScores;

  const totalCount =
    data?.anyGame?.myBestByPlayerCards?.totalCount || totalCountFromProps;

  const loadingItemsToDisplay = Math.min(
    totalCount - cardsWithPlayerGameScores.length,
    // 10 for current pagination, 5 as gameWithCardsPreview contains first 5 cards
    paginatedcardsWithPlayerGameScores ? 10 : 5
  );

  return (
    <Horizontal center>
      <Appearances>
        {cardsWithPlayerGameScores.map((cardWithPlayerGameScores, index) => {
          return (
            <AppearanceAvatarWithScore
              // TODO: update when type have id/slug
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              cardWithPlayerGameScoreInterface={cardWithPlayerGameScores}
              onClick={() =>
                onCardClick(cardWithPlayerGameScores.card.anyPlayer.slug)
              }
            />
          );
        })}
        {!!loading &&
          range(loadingItemsToDisplay).map(i => {
            return <Loader key={i} />;
          })}
        {totalCount > cardsWithPlayerGameScores.length && (
          <StyledBrandXS
            as={ButtonBase}
            onClick={() => {
              setUsePaginatedData(true);
              loadMore(false, {
                after: data?.anyGame?.myBestByPlayerCards?.pageInfo.endCursor,
              });
            }}
            color="var(--c-white)"
          >
            +{totalCount - cardsWithPlayerGameScores.length}
          </StyledBrandXS>
        )}
      </Appearances>
    </Horizontal>
  );
};

GameCardsPreview.fragments = {
  gameWithCardsPreview: gql`
    fragment GameCardsPreview_gameWithCardsPreview on GameWithCardsPreview {
      id
      game {
        id
      }
      cardsWithPlayerGameScores {
        id
        ...GameCardsPreview_cardWithPlayerGameScoreInterface
      }
      totalCount
    }
    ${cardWithPlayerGameScoreInterfaceFragment}
  ` as TypedDocumentNode<GameCardsPreview_gameWithCardsPreview>,
};
